import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter } from 'lodash-es';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  AUTH_ERROR_CODE,
  CARD_ACCOUNT_ERROR_CODES,
  IDV_ERROR_CODES,
  MESSAGE,
  SYSTEM_MAINTENANCE,
  TRANSACTION_ERROR_CODES,
} from '@shared/constants';
import { NotificationService, captureSentryException } from '@shared/services';
import { AuthActions, LoginPageActions } from '@shared/store';
import { ErrorUtils } from '@shared/utils';

import { CUSTOM_ERROR_MESSAGE, IS_EXTENDED_ERRORS, SKIP_ERROR_NOTIFICATION, SKIP_SESSION_LOGOUT } from './http-context-tokens';

export * from './http-context-tokens';

const excluded401Urls = ['/validate'].map((urlPattern) => new RegExp(urlPattern, 'i'));

// A 401 here means the token the auth provider just issued was rejected, which is a defect rather
// than an ended session, so these are the only 401s still reported to Sentry.
const captured401Urls = ['/exchange-token'].map((urlPattern) => new RegExp(urlPattern, 'i'));

// The business account token refresh runs on rehydrate, so a portal reopened on an expired session
// answers it with a 400 right after the 401s; captureErrorByHttpCode already treats this endpoint's
// failures as the session ending.
const businessAccountTokenUrl = /\/business-accounts\/[^/]+\/token(?:\?|$)/i;

const BUSINESS_ERROR_CODES = [...TRANSACTION_ERROR_CODES, ...CARD_ACCOUNT_ERROR_CODES, ...IDV_ERROR_CODES];

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  router = inject(Router);

  constructor(
    private notificationService: NotificationService,
    private store: Store,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err) => {
        let errorOutput = (err && err.status !== 0 && err.status) || err.statusText;

        // PCI password-expiry: hand the body to the login effect to route the user to set a new
        // password. Must bypass the 403 logout/notification handling below.
        if (err && err.status === HttpStatusCode.Forbidden && err.error?.errorCode === AUTH_ERROR_CODE.passwordExpired) {
          return throwError(() => err.error);
        }

        if (err && err.status === HttpStatusCode.ServiceUnavailable && err.error === SYSTEM_MAINTENANCE) {
          if (err.url.includes('tenant-info')) {
            return throwError(() => err.error);
          } else {
            this.store.dispatch(LoginPageActions.loggedInRedirectToMaintenance({}));
          }
        }

        if (
          err &&
          (err.status === HttpStatusCode.BadRequest ||
            err.status === HttpStatusCode.UnprocessableEntity ||
            err.status === HttpStatusCode.Conflict) &&
          err.error &&
          err.error.message
        ) {
          errorOutput = err.error.message;
        }

        // catch by error code
        if (err.error && TRANSACTION_ERROR_CODES.includes(err.error.errorCode)) {
          const { errorCode, message, _embedded = {} } = err.error;

          // If insufficient-limit with approval request created, return full error for handling
          // The approval request summary will be shown instead of error
          const hasApprovalRequest = errorCode === 'insufficient-limit' && message?.includes('Approval request');
          if (hasApprovalRequest) {
            return throwError(() => JSON.stringify(err.error));
          }

          errorOutput = errorCode + this.captureLimitErrors(errorCode, message, _embedded);
        }

        if (err.error && CARD_ACCOUNT_ERROR_CODES.includes(err.error.errorCode)) {
          errorOutput = err.error?.errorCode || err.error?.message;
        }

        if (err.error && IDV_ERROR_CODES.includes(err.error.errorCode)) errorOutput = JSON.stringify(err.error);

        // return the entire error object if IS_EXTENDED_ERRORS is set to true in the request context
        // and the error status is not [401, 403, 404, 500]
        if (
          request.context.get(IS_EXTENDED_ERRORS) === true &&
          ![HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden, HttpStatusCode.InternalServerError, HttpStatusCode.NotFound].includes(
            err.status,
          )
        ) {
          errorOutput = JSON.stringify(err.error);
        }

        const customErrorMessage = request.context.get(CUSTOM_ERROR_MESSAGE);

        this.captureErrorByHttpCode(err.status, errorOutput, request, request.context.get(SKIP_ERROR_NOTIFICATION) || !!customErrorMessage);

        // Capture sits here rather than inside captureErrorByHttpCode so that reaching Sentry does
        // not depend on a status having a UX branch.
        if (this.shouldCaptureForSentry(err, request)) {
          this.captureErrorForSentry(request.url, this.sentryMessageFor(err), err.status);
        }

        if (customErrorMessage) {
          errorOutput = customErrorMessage;
          this.notificationService.displayError(customErrorMessage, '');
        }

        return throwError(() => errorOutput);
      }),
    );
  }

  captureErrorByHttpCode(status: HttpStatusCode, errorMessage: string, request: HttpRequest<unknown>, skipNotification: boolean) {
    switch (status) {
      case HttpStatusCode.Unauthorized:
        const passThrough = !!excluded401Urls.find((regex) => regex.test(request.url));

        if (passThrough) {
          return;
        }

        this.store.dispatch(AuthActions.logout({ params: { isReturnUrl: true } }));

        if (!skipNotification)
          this.notificationService.displayError(MESSAGE.SESSION_EXPIRED_NOTIFICATION, MESSAGE.SESSION_EXPIRED_NOTIFICATION_TITLE);

        break;

      case HttpStatusCode.Forbidden:

      case HttpStatusCode.InternalServerError:
        if (
          (request.url.includes('token') && !request.url.includes('exchange-token')) ||
          request.url.includes('linked-business-accounts')
        ) {
          if (request.context.get(SKIP_SESSION_LOGOUT)) {
            break;
          }

          this.store.dispatch(AuthActions.logout({ params: { isReturnUrl: true } }));

          if (!skipNotification)
            this.notificationService.displayError(MESSAGE.SESSION_EXPIRED_NOTIFICATION, MESSAGE.SESSION_EXPIRED_NOTIFICATION_TITLE);
        } else if (status === HttpStatusCode.Forbidden && !request.url.includes('forgot-password') && !skipNotification) {
          this.notificationService.displayError(MESSAGE.PERMISSION_DENIED_NOTIFICATION, MESSAGE.PERMISSION_DENIED_NOTIFICATION_TITLE);
        } else if (status === HttpStatusCode.InternalServerError) {
          if (!skipNotification) this.notificationService.displayError(MESSAGE.SESSION_UNAVAILABLE_NOTIFICATION, '');

          ErrorUtils.catchError(request.url, errorMessage);
        }
        break;

      case HttpStatusCode.NotFound:
        if (!skipNotification) this.notificationService.displayError(MESSAGE.RESOURCE_NOT_FOUND_ERROR, '');
        break;

      default:
        break;
    }
  }

  /**
   * The message Sentry sees is derived from the response rather than reused from errorOutput.
   * errorOutput can be a serialized error body (IDV and extended-error requests stringify the whole
   * payload), and a server payload should not cross to a third-party service unreviewed. The
   * server's own display message is what the user is already shown, so it is safe and useful.
   */
  sentryMessageFor(err: HttpErrorResponse): string {
    const message = err.error?.message;

    return typeof message === 'string' && message ? message : err.statusText;
  }

  /**
   * Everything the server actually failed on, which is every status >= 400. Exclusions are the
   * flows the portal drives deliberately - they are expected states, not failures - plus status 0,
   * which a closed tab or an offline client produces in volume and which carries no diagnosis.
   */
  shouldCaptureForSentry(err: HttpErrorResponse, request: HttpRequest<unknown>): boolean {
    if (!err || !err.status || err.status < HttpStatusCode.BadRequest) {
      return false;
    }

    if (err.status === HttpStatusCode.ServiceUnavailable && err.error === SYSTEM_MAINTENANCE) {
      return false;
    }

    // A business rule the server enforced correctly is an expected state, not a failure. These are
    // also the highest-frequency 4xx in the portal, so capturing them would bury the real failures.
    if (BUSINESS_ERROR_CODES.includes(err.error?.errorCode)) {
      return false;
    }

    if (err.status === HttpStatusCode.Forbidden && err.error?.errorCode === AUTH_ERROR_CODE.passwordExpired) {
      return false;
    }

    // A 401 is the auth flow working: the session ended and captureErrorByHttpCode logs the user out,
    // so every request in flight at that moment would otherwise open its own issue. The SDK's XHR
    // breadcrumbs still record these requests on any event that follows.
    if (err.status === HttpStatusCode.Unauthorized) {
      return captured401Urls.some((regex) => regex.test(request.url));
    }

    if (err.status === HttpStatusCode.BadRequest && businessAccountTokenUrl.test(request.url)) {
      return false;
    }

    return true;
  }

  captureErrorForSentry(requestURL: string, errorMessage: string, errorStatusCode: HttpStatusCode) {
    captureSentryException({
      requestURL,
      errorMessage,
      pageURL: location.href,
      errorStatusCode,
      timestamp: new Date().toUTCString(),
    });
  }

  captureLimitErrors(errorCode: string, message: string, _embedded: Record<string, { scopeType: string; limitType: string }[]>): string {
    const messageLimits = message.match(/[^([]*(?:provider|settlement|solution|sponsor)[^)\]]*/gi);

    let limits = '';

    if (errorCode === 'insufficient-limit' && _embedded['limit-utilizations']?.length) {
      limits = filter(_embedded['limit-utilizations'], ({ scopeType }) => ['CHILD_BA', 'FA_KFA'].includes(scopeType))
        .sort((a, b) => (a.scopeType > b.scopeType ? 1 : -1))
        .map(({ limitType, scopeType }) => `${scopeType}:${limitType}`)
        .join('::');
    }

    if (messageLimits?.length) {
      limits = (limits.length ? `${limits}::` : '') + messageLimits.join('::');
    }

    return limits ? `__${limits.toLowerCase()}` : '';
  }
}
